/* eslint complexity: ["error", 17] */
import PropTypes from 'prop-types';

import { Box, CapitalOneLogoPrimaryDark, Flex } from '@cof/plastic-components';
import { css, styled, useTheme } from 'styled-components';
import { useBrand } from '../../BrandContext';
import { withText } from '../../text/textStore';
import { useAggregatorConsumerName } from '../../utilities/aggregatorConsumerNameProvider';
import { hasAggregatorLogo, isAggregatorConsumer, isHosted } from '../../utilities/utils';
import Logo from '../Logo';
import aggregatorsLogoMap from '../Logo/AggregatorsLogo/aggregatorsLogoMap';
import partners from '../../partners';

export const StyledHeader = styled.header`
    ${({ theme, $isAggregatorWithLogo, $isDeclinePage, $isObLanding, $isDirectObLanding }: any) => css`
        background-color: ${$isAggregatorWithLogo ? theme.colors.global.white : theme.colors.capitalOne.blue750};
        border: ${$isAggregatorWithLogo ? theme.colors.global.white : theme.colors.capitalOne.blue750};
        margin: 0;
        padding: ${$isDeclinePage ? `${theme.space.lg} 0 ${theme.space.md} 0` : theme.space.sm};
        ${$isDirectObLanding &&
        css`
            padding: ${theme.space.md};
        `}
        text-align: center;
        font-size: 0;
        position: relative;
        z-index: 1;
        ${theme.breakpoints.mq.tabletMin} {
            padding: ${theme.space.lg} 0 ${theme.space.lg} ${theme.space.lg};
            ${$isDeclinePage &&
            css`
                padding: ${theme.space.xl} 0 ${theme.space.lg} 0;
            `}
            ${$isObLanding &&
            css`
                padding: ${theme.space.lg} 0 ${theme.space.lg} 0;
            `}
        }

        ${theme.components.StyledHeader}
    `}
`;

export const LogoWrapper = styled(Flex)`
    ${({ theme, $hide, $isDeclinePage }: any) => css`
        max-width: 67.4rem;
        padding: ${$isDeclinePage ? `0` : `${theme.space.xs} ${theme.space.xl} 0 ${theme.space.xs}`};
        margin: auto;
        visibility: ${$hide && 'hidden'};
    `}
`;

const StyledLogo = styled(Logo)`
    ${({ theme, $isDeclinePage }: any) => css`
        ${$isDeclinePage && theme.components.LogoStyled}
    `}
`;

// eslint-disable-next-line complexity
const Header = ({
    // @ts-expect-error TS7031: Binding element 'text' implicitly has an 'any' type.
    Text: text,
    mainPage = false,
    hideLogo = false,
    isDeclinePage = false,
    isObLanding = false,
    isObPending = false
}) => {
    const brand = useBrand();
    const theme = useTheme();
    // @ts-expect-error TS2488: Type '' must have a '[Symbol.iterator]()' method that returns an iterator.

    const [aggregatorConsumerName] = useAggregatorConsumerName();
    const isAggregatorWithLogo = hasAggregatorLogo(aggregatorConsumerName);
    const variant = isAggregatorWithLogo ? 'dark' : 'light';
    const isSimpleHeader = isHosted(brand) && !isAggregatorConsumer(aggregatorConsumerName);
    const consumerName = new URLSearchParams(window.location.search).get('consumerName');
    const isDirectObLanding = isObLanding && !consumerName;
    let stylingMap = ['center', null, 'center'];
    if (isDeclinePage) {
        stylingMap = ['center', null, null];
    } else if (isObLanding) {
        stylingMap = ['left', null, 'center'];
    }

    return (
        <StyledHeader
            // @ts-expect-error TS2769: No overload matches this call.
            $isAggregatorWithLogo={isAggregatorWithLogo}
            $isDeclinePage={isDeclinePage}
            $isObLanding={isObLanding}
            $isDirectObLanding={isDirectObLanding}
            $isObPending={isObPending} // This is processed by StyledHeader in ocean/components.js
            data-qa-id="header"
            $mainPage={mainPage}
        >
            {isSimpleHeader && mainPage ? (
                // @ts-expect-error TS2769: No overload matches this call.
                <LogoWrapper data-qa-id="header-logo-wrapper" $hide={hideLogo}>
                    <Flex width="100%" height="100%" justifyContent={['left', null, 'center']} alignItems="center">
                        <a
                            data-qa-id="header-link"
                            href={text({ path: 'Link' })}
                            aria-label={text({ path: 'AriaLabel' })}
                        >
                            <StyledLogo variant={variant} $isDeclinePage={isDeclinePage} $brand={brand} />
                        </a>
                        {isAggregatorWithLogo && (
                            <Box
                                borderLeft="1px solid black"
                                marginLeft="10px"
                                paddingLeft="10px"
                                data-qa-id="aggregator-logo-wrapper"
                            >
                                {/* @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '  */}
                                {aggregatorsLogoMap[aggregatorConsumerName]}
                            </Box>
                        )}
                    </Flex>
                </LogoWrapper>
            ) : (
                <Flex
                    data-qa-id="header-logo-wrapper"
                    width="100%"
                    height="100%"
                    justifyContent={stylingMap}
                    alignItems="center"
                >
                    <a data-qa-id="header-link" href={text({ path: 'Link' })} aria-label={text({ path: 'AriaLabel' })}>
                        <StyledLogo variant={variant} $isDeclinePage={isDeclinePage} $brand={brand} />
                    </a>
                    {isAggregatorWithLogo && (
                        <Box
                            borderLeft="1px solid black"
                            marginLeft="10px"
                            paddingLeft="10px"
                            data-qa-id="aggregator-logo-wrapper"
                        >
                            {/* @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '  */}
                            {aggregatorsLogoMap[aggregatorConsumerName]}
                        </Box>
                    )}
                    {brand !== partners.CAPITAL_ONE && isObLanding && (
                        <Box
                            borderLeft={`1px solid`}
                            // @ts-expect-error TS2769: No overload matches this call.
                            borderColor={theme.pages.openBanking.borderColor}
                            marginLeft="16px"
                            paddingLeft="16px"
                            data-qa-id="partner-ob-logo-wrapper"
                        >
                            <CapitalOneLogoPrimaryDark width="120px" />
                        </Box>
                    )}
                </Flex>
            )}
        </StyledHeader>
    );
};

Header.propTypes = {
    Text: PropTypes.func.isRequired,
    mainPage: PropTypes.bool,
    hideLogo: PropTypes.bool,
    isDeclinePage: PropTypes.bool,
    isObLanding: PropTypes.bool,
    isObPending: PropTypes.bool
};

const WrappedHeader = withText('Header')(Header);

WrappedHeader.displayName = 'Header';

export default WrappedHeader;
